import React from 'react';
import {
  AppColors,
  DateDisplay,
  EnumValueDisplay,
  formatTimeSpanValue,
  LastSeenDisplay,
  normalizeDate,
} from '@lib/common';
import { ValueText } from './styled-table';
import { PowerStatus, PropertyDevice } from 'app/state';
import { Box, Chip } from '@material-ui/core';

export function normalizeIdentifier(value?: string | null): string {
  return value?.trim().toLowerCase() ?? '';
}

export function isGuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function identifiersMatch(a?: string | null, b?: string | null): boolean {
  return normalizeIdentifier(a) === normalizeIdentifier(b);
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  const formattedTimeSpan = formatTimeSpanValue(value);
  if (formattedTimeSpan !== undefined) {
    return formattedTimeSpan;
  }

  return '-';
}

const DATE_KEYS = new Set<keyof PropertyDevice>([
  'lastSeen',
  'lastOffline',
  'lastOnline',
  'lastBatteryLow',
  'lastPowerLoss',
]);

function isDateKey(key: keyof PropertyDevice) {
  return DATE_KEYS.has(key);
}

function getPowerStatusChipStyle(powerStatus?: PowerStatus) {
  switch (powerStatus) {
    case PowerStatus.Online:
      return { backgroundColor: AppColors.Green, color: AppColors.White };
    case PowerStatus.BatteryIssues:
      return { backgroundColor: AppColors.Red, color: AppColors.White };
    case PowerStatus.Offline:
    default:
      return { backgroundColor: AppColors.Gray, color: AppColors.White };
  }
}

export function renderValue<K extends keyof PropertyDevice>(
  key: K,
  value: PropertyDevice[K],
  device: PropertyDevice
) {
  if (key === 'powerStatus') {
    return (
      <Box display="flex" alignItems="center" gridGap={4}>
        <Chip
          size="small"
          label={<EnumValueDisplay value={device.powerStatus} />}
          style={getPowerStatusChipStyle(device.powerStatus)}
        />
        {device.powerStatus === PowerStatus.Offline && device.lastSeen && (
          <LastSeenDisplay lastSeen={device.lastSeen} prefix="Since" variant="body2" />
        )}
      </Box>
    );
  }

  if (key === 'thingType') {
    return <EnumValueDisplay value={device.thingType} variant="body2" />;
  }

  if (isDateKey(key)) {
    return (
      <DateDisplay date={normalizeDate(value)} dateFormat="MM/dd/yyyy hh:mm aa" variant="body2">
        -
      </DateDisplay>
    );
  }

  return <ValueText>{formatValue(value)}</ValueText>;
}
